import { PhoneIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EnhancedDynamicTablePagination from "../common/CommonTable/EnhancedDynamicTablePagination";
import CommonButton from "../common11/Buttons/CommonButton";
import TableButton from "../common11/Buttons/TableButton";
import CommonDatePicker from "../common11/formInputs/CommonDatePicker";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../common11/toastUtils";
import {
  callVisit,
  fetchMedicalDepartments,
  fetchMedicalDoctors,
  fetchMedicalRecordQueue,
  setMedicalRecordFilters,
} from "../../store/slices/medicalRecordSlice";

const getDefaultFilters = () => {
  const today = new Date().toISOString().split("T")[0];
  return {
    patientName: "",
    departmentId: "",
    doctorId: "",
    fromDate: today,
    toDate: today,
  };
};

const STATUS_FILTER_CONFIG = {
  All: {
    selected: "bg-[#217E90] text-white border-[#217E90] shadow-sm",
    unselected: "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200",
  },
  "In Queue": {
    selected: "bg-blue-500 text-white border-blue-500 shadow-sm",
    unselected: "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200",
  },
  Called: {
    selected: "bg-amber-500 text-white border-amber-500 shadow-sm",
    unselected: "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200",
  },
  "On going": {
    selected: "bg-green-500 text-white border-green-500 shadow-sm",
    unselected: "bg-green-100 text-green-800 border-green-300 hover:bg-green-200",
  },
};

function MedicalRecordQueue({
  hideTitle = false,
  showFilters: showFiltersProp,
  setShowFilters: setShowFiltersProp,
  tableHeading: tableHeadingProp,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const rows = useSelector((state) => state.medicalRecord.rows);
  const loading = useSelector((state) => state.medicalRecord.loading);
  const departments = useSelector((state) => state.medicalRecord.departments);
  const doctors = useSelector((state) => state.medicalRecord.doctors);
  const storeFilters = useSelector((state) => state.medicalRecord.filters);

  const [filters, setFilters] = useState(getDefaultFilters());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState("All");
  const [internalShowFilters, setInternalShowFilters] = useState(false);
  const showFilters =
    setShowFiltersProp !== undefined ? showFiltersProp : internalShowFilters;
  const setShowFilters =
    setShowFiltersProp !== undefined ? setShowFiltersProp : setInternalShowFilters;

  const today = filters.fromDate;
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      SearchByPatient: "",
      department: null,
      doctorSearch: null,
      fromDate: today,
      toDate: today,
    },
  });

  const loadQueue = (nextFilters = filters, nextStatus = statusFilter) => {
    const payload = {
      fromDate: nextFilters.fromDate,
      toDate: nextFilters.toDate,
      departmentId: nextFilters.departmentId || undefined,
      doctorId: nextFilters.doctorId || undefined,
      q: nextFilters.patientName || undefined,
      status: nextStatus,
    };
    dispatch(setMedicalRecordFilters(payload));
    dispatch(fetchMedicalRecordQueue(payload));
  };

  useEffect(() => {
    dispatch(fetchMedicalDepartments(""));
    dispatch(fetchMedicalDoctors(""));
    loadQueue(filters, "All");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const departmentOptions = useMemo(() => departments, [departments]);
  const doctorOptions = useMemo(() => doctors, [doctors]);

  const handleSearch = (data) => {
    const depId = data.department?.value || "";
    const docId = data.doctorSearch?.value || "";
    const patientQ =
      typeof data.SearchByPatient === "string"
        ? data.SearchByPatient
        : data.SearchByPatient?.label || data.SearchByPatient?.value || "";
    const newFilters = {
      patientName: patientQ,
      departmentId: depId,
      doctorId: docId,
      fromDate: data.fromDate,
      toDate: data.toDate,
    };
    setFilters(newFilters);
    setPage(0);
    loadQueue(newFilters, statusFilter);
    showSuccessToast("Queue refreshed successfully", { duration: 2000 });
  };

  const handleResetFilters = () => {
    const defaults = getDefaultFilters();
    reset({
      SearchByPatient: "",
      department: null,
      doctorSearch: null,
      fromDate: defaults.fromDate,
      toDate: defaults.toDate,
    });
    setStatusFilter("All");
    setFilters(defaults);
    setPage(0);
    loadQueue(defaults, "All");
    showInfoToast("Filters reset", { duration: 2000 });
  };

  const handleCallPatient = async (row) => {
    try {
      const called = await dispatch(callVisit(row.visitId)).unwrap();
      showInfoToast(`Calling patient: ${called.patientName || row.patientName}`, {
        duration: 2000,
      });
      navigate("/case-paper", {
        state: {
          visitId: called.visitId || row.visitId,
          patientId: called.regId || row.regId,
          patientName: called.patientName || row.patientName,
          patient: {
            ...row,
            ...called,
            uhid: called.uhid || row.uhid,
            opdNo: called.opdNo || row.opdNo || row.visitNo,
            doctorName: called.doctorName || row.doctorName || row.doctor,
            visitId: called.visitId || row.visitId,
            regId: called.regId || row.regId,
            casePaperId: called.casePaperId || row.casePaperId,
          },
          from: window.location.pathname,
        },
      });
      loadQueue(filters, statusFilter);
    } catch (error) {
      showErrorToast(error || "Failed to call patient");
    }
  };

  const getDerivedStatus = (row) => row.queueStatus || "In Queue";

  const renderActions = (row) => (
    <div className="flex items-center gap-0.5 justify-center">
      <TableButton
        icon={<PhoneIcon className="w-4 h-4" />}
        tooltip="Call patient & open case paper"
        onClick={() => handleCallPatient(row)}
        className="text-[#217E90] hover:bg-[#E9F5FE]"
      />
    </div>
  );

  const renderCustomColumns = {
    status: (row) => {
      const status = getDerivedStatus(row);
      const getStatusStyle = (statusValue) => {
        switch (statusValue) {
          case "In Queue":
            return "bg-blue-50 text-blue-700 border border-blue-300";
          case "Called":
            return "bg-amber-50 text-amber-700 border border-amber-300";
          case "On going":
            return "bg-green-50 text-green-700 border border-green-300";
          default:
            return "bg-gray-50 text-gray-700 border border-gray-300";
        }
      };
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(
            status
          )}`}
        >
          <span
            className="inline-block w-2 h-2 rounded-full mr-1.5"
            style={{ backgroundColor: "currentColor" }}
          />
          {status}
        </span>
      );
    },
    patientName: (row) => row.patientName || "N/A",
    age: (row) => row.age || "N/A",
    gender: (row) => row.gender || "N/A",
    department: (row) => row.department || "N/A",
    doctor: (row) => row.doctor || "N/A",
  };

  const dataResult = useMemo(() => {
    const filtered =
      statusFilter === "All"
        ? rows
        : rows.filter(
            (row) =>
              getDerivedStatus(row).toLowerCase() === statusFilter.toLowerCase()
          );
    return (filtered || []).map((row) => ({
      ...row,
      status: getDerivedStatus(row),
    }));
  }, [rows, statusFilter]);

  const getStatusCount = (label) => {
    if (label === "All") return rows.length;
    return rows.filter(
      (row) => getDerivedStatus(row).toLowerCase() === label.toLowerCase()
    ).length;
  };

  const handleStatusClick = (status) => {
    setStatusFilter(status);
    setPage(0);
    loadQueue(filters, status);
  };

  return (
    <div className={hideTitle ? "" : "p-3 sm:p-4 lg:p-6"}>
      {!hideTitle && (
        <div className="sticky top-0 z-10 bg-[#F4F9FD] pt-1 -mt-1 pb-3 sm:pb-4 mb-0 gap-2 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base sm:text-lg font-bold text-[#217E90]">
            Patient Queue
          </h2>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden bg-[#217E90] text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      )}

      <div
        className={`${showFilters ? "block" : "hidden"} lg:block rounded-lg border border-gray-200`}
      >
        <form onSubmit={handleSubmit(handleSearch)}>
          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2 sm:gap-3 mb-3">
              <div className="sm:col-span-2 lg:col-span-2">
                <SearchDropdown
                  name="SearchByPatient"
                  control={control}
                  dataArray={[]}
                  label="Search by patient name, Mobile no."
                  isSearchable
                  isClearable
                  error={errors.SearchByPatient}
                />
              </div>
              <div className="lg:col-span-1">
                <SearchDropdown
                  name="department"
                  label="Department"
                  control={control}
                  dataArray={departmentOptions}
                  error={errors.department}
                  disabled={isSubmitting}
                  isSearchable
                  isClearable
                  onInputChange={(q) => dispatch(fetchMedicalDepartments(q))}
                />
              </div>
              <div className="lg:col-span-1">
                <SearchDropdown
                  name="doctorSearch"
                  label="Doctor"
                  control={control}
                  dataArray={doctorOptions}
                  error={errors.doctorSearch}
                  disabled={isSubmitting}
                  isSearchable
                  isClearable
                  onInputChange={(q) => dispatch(fetchMedicalDoctors(q))}
                />
              </div>
              <div className="lg:col-span-1">
                <CommonDatePicker
                  name="fromDate"
                  type="date"
                  label="From Date"
                  control={control}
                  error={errors.fromDate}
                  maxDate={watch("toDate")}
                />
              </div>
              <div className="lg:col-span-1">
                <CommonDatePicker
                  name="toDate"
                  type="date"
                  label="To Date"
                  control={control}
                  error={errors.toDate}
                  minDate={watch("fromDate")}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:col-span-2 lg:col-span-1">
                <CommonButton
                  label={loading ? "Loading..." : "Search"}
                  type="submit"
                  disabled={isSubmitting || loading}
                  className="bg-[#217E90] hover:bg-[#1a5e70] text-white w-full sm:flex-1"
                  size="sm"
                />
                <CommonButton
                  label="Reset"
                  type="button"
                  onClick={handleResetFilters}
                  disabled={isSubmitting}
                  variant="danger-outline"
                  size="sm"
                  className="flex-1 sm:flex-none px-3 md:px-5 text-xs md:text-sm"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pb-1">
              {["All", "In Queue", "Called", "On going"].map((status) => {
                const style = STATUS_FILTER_CONFIG[status] || STATUS_FILTER_CONFIG.All;
                const isSelected = statusFilter === status;
                const count = getStatusCount(status);
                return (
                  <button
                    key={status}
                    type="button"
                    className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-md transition-all duration-200 flex-shrink-0 border font-medium ${
                      isSelected ? style.selected : style.unselected
                    }`}
                    onClick={() => handleStatusClick(status)}
                  >
                    {status} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </div>

      <div className="mt-3">
        <EnhancedDynamicTablePagination
          tableHeading={tableHeadingProp || "Medical record list"}
          dataResult={dataResult}
          setDataResult={() => {}}
          fetchData={() => {}}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={(value) => {
            setRowsPerPage(value);
            setPage(0);
          }}
          count={dataResult.length}
          removeHeaders={[
            "visitId",
            "regId",
            "patientId",
            "contact",
            "uhid",
            "departmentId",
            "doctorId",
            "doctorName",
            "opdNo",
            "visitDate",
            "visitTime",
            "casePaperId",
            "queueStatus",
            "isChecked",
          ]}
          columnHeaderLabels={{
            status: "Status",
            patientName: "Patient Name",
            age: "Age",
            gender: "Gender",
            department: "Department",
            doctor: "Doctor",
            tokenNo: "Token No",
            visitNo: "Visit No",
          }}
          columnsAfterActions={["status"]}
          renderActions={renderActions}
          renderCustomColumns={renderCustomColumns}
          stickyActions
          highlightRow
          customRowBgColor="#E9F5FE"
          customRowBorderColor="#217E90"
        />
      </div>
      {!loading && storeFilters?.fromDate && (
        <p className="mt-1 text-[10px] text-gray-400 px-1">
          Showing visits {storeFilters.fromDate} to {storeFilters.toDate}
        </p>
      )}
    </div>
  );
}

export default MedicalRecordQueue;
